import * as React from 'react';

import type Sentry from '@sentry/browser';
import type { EventHint } from '@sentry/types';
import { Subject, type Subscription } from 'rxjs';

import type { ErrorAlertProps } from 'common/components/ErrorAlert/ErrorAlert';
import ErrorAlert from 'common/components/ErrorAlert/ErrorAlert';

import { withDialog, type WithDialogProps } from '../common/components/core/DialogProvider';
import { isTrackedError } from './Error';
import SentrySeverity from './SentrySeverity';

interface SentryEvent {
  event?: Sentry.Event;
  hint: EventHint;
}

export interface ErrorState {
  severity: Sentry.SeverityLevel;
  exception: Error;
  eventId?: string;
}

export const showErrorBoundarySubject = new Subject<SentryEvent>();

export interface ErrorBoundaryProps extends WithDialogProps {}

export interface ErrorBoundaryState {
  error: ErrorState | null;
}

function getSeverity(error: Error): Sentry.SeverityLevel {
  return isTrackedError(error) ? SentrySeverity.Warning : SentrySeverity.Fatal;
}

export class ErrorBoundary extends React.Component<React.PropsWithChildren<ErrorBoundaryProps>, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    error: null,
  };

  private showErrorBoundarySubscription: Subscription;

  static getDerivedStateFromError(error: Error) {
    return { error: { exception: error, severity: getSeverity(error) } };
  }

  public openErrorDialog(): void {
    if (this.state.error) {
      const errorProps = this.getErrorDialogProps(this.state.error);
      this.props.openDialog<ErrorAlertProps>(ErrorAlert, errorProps);
    }
  }

  public get defaultErrorDialogProps() {
    return {
      onClose: () => this.setState({ error: null }),
      error: this.state.error,
      canOutsideClickClose: true,
    };
  }

  public getErrorDialogProps(error: ErrorState): ErrorAlertProps {
    const props = this.defaultErrorDialogProps as ErrorAlertProps;
    if (isTrackedError(error.exception)) {
      const trackedError = error.exception;
      props.content = trackedError.extras.content;
      props.title = trackedError.extras.title;
    }
    return props;
  }

  public componentDidCatch(error: Error) {
    this.setState({
      error: {
        exception: error,
        severity: getSeverity(error),
      },
    });
  }

  public componentDidMount() {
    this.showErrorBoundarySubscription = showErrorBoundarySubject.subscribe(({ event, hint }) => {
      if (hint.originalException) {
        this.setState({
          error: {
            eventId: event && event.event_id!,
            exception: hint.originalException as Error,
            severity: getSeverity(hint.originalException as Error),
          },
        });
      }
    });
  }

  public componentDidUpdate(_prevProps: ErrorBoundaryProps, prevState: ErrorBoundaryState) {
    if (!prevState.error && this.state.error?.severity === SentrySeverity.Warning) {
      this.openErrorDialog();
    }
  }

  public componentWillUnmount() {
    this.showErrorBoundarySubscription.unsubscribe();
  }

  public render() {
    return this.props.children;
  }
}

export default withDialog(ErrorBoundary) as React.ComponentType<React.PropsWithChildren<unknown>>;
